import * as PopoverPrimitive from '@radix-ui/react-popover';
import { CheckIcon, ChevronDownIcon } from 'lucide-react';
import { type ComponentType, type CSSProperties, type KeyboardEvent, useId, useState } from 'react';

import { cn } from '@/design-system/lib/cn';

/**
 * Opção da seleção múltipla. `icon` e `iconClassName` (a cor do ícone, por token) deixam
 * a opção se reconhecer como o chip ou o selo que ela representa; `hint` é o texto
 * discreto à direita (a contagem de itens, por exemplo).
 */
export type MultiSelectOption = {
  value: string;
  label: string;
  icon?: ComponentType<{ className?: string; style?: CSSProperties; 'aria-hidden'?: boolean }>;
  iconClassName?: string;
  /** Variáveis de cor que são dado (`toneStyle`: a cor da série), lidas por `text-tone`. */
  iconStyle?: CSSProperties;
  hint?: string | number;
};

export type MultiSelectProps = {
  values: readonly string[];
  onValuesChange: (values: string[]) => void;
  /** Na ordem dada: quem usa já sabe a ordem que faz sentido (uma sequência de situações). */
  options: readonly MultiSelectOption[];
  'aria-label': string;
  /** Texto do botão sem nada marcado ("Todas as situações"). */
  placeholder?: string;
  /** Texto do botão com mais de uma opção marcada. Padrão: "N selecionados". */
  summary?: (count: number) => string;
  size?: 'md' | 'sm';
  className?: string;
  disabled?: boolean;
  id?: string;
};

const MAX_TRIGGER_ICONS = 3;

/**
 * Seleção múltipla do design: o mesmo botão `combobox` e a mesma lista em popover do
 * `SimpleSelect`, mas cada opção é um `checkbox` e a lista continua aberta ao marcar.
 * As opções saem na ordem recebida, com ícone colorido e contagem opcionais; o botão
 * mostra o ícone e o nome da única marcada, ou os ícones e o resumo de várias. "Limpar"
 * no rodapé desmarca tudo. Teclado: setas, Home/End, Enter ou espaço marca, Esc e Tab.
 */
export function MultiSelect({
  values,
  onValuesChange,
  options,
  'aria-label': ariaLabel,
  placeholder = 'Todos',
  summary = (count) => `${count} selecionados`,
  size = 'md',
  className,
  disabled,
  id,
}: MultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const uid = useId();
  const listId = `${uid}-list`;
  const optionId = (i: number) => `${uid}-opt-${i}`;
  const selected = options.filter((o) => values.includes(o.value));
  const only = selected.length === 1 ? selected[0] : undefined;
  const triggerIcons = selected.filter((o) => o.icon).slice(0, MAX_TRIGGER_ICONS);

  const toggle = (option: MultiSelectOption) =>
    onValuesChange(
      values.includes(option.value) ? values.filter((v) => v !== option.value) : [...values, option.value]
    );

  const move = (to: number) => setActive(Math.min(options.length - 1, Math.max(0, to)));

  const onListKey = (e: KeyboardEvent) => {
    const keys: Record<string, () => void> = {
      ArrowDown: () => move(active + 1),
      ArrowUp: () => move(active - 1),
      Home: () => move(0),
      End: () => move(options.length - 1),
      Enter: () => {
        const option = options[active];

        if (option) toggle(option);
      },
      ' ': () => {
        const option = options[active];

        if (option) toggle(option);
      },
    };
    const action = keys[e.key];

    if (action) {
      e.preventDefault();
      action();
    } else if (e.key === 'Tab') setOpen(false);
  };

  const openChange = (next: boolean) => {
    if (next)
      setActive(
        Math.max(
          0,
          options.findIndex((o) => values.includes(o.value))
        )
      );

    setOpen(next);
  };

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={openChange} modal>
      <PopoverPrimitive.Trigger asChild>
        <button
          type="button"
          role="combobox"
          id={id}
          aria-label={ariaLabel}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          disabled={disabled}
          onKeyDown={(e) => {
            if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
              e.preventDefault();
              openChange(true);
            }
          }}
          data-slot="select-trigger"
          className={cn(
            'flex w-full min-w-0 items-center justify-between gap-2 rounded-control border bg-white pr-2.5 pl-3 text-left text-ink',
            'hover:border-line-dashed disabled:opacity-60',
            open ? 'border-primary-focus ring-3 ring-primary-focus/15' : 'border-line-input',
            size === 'sm' ? 'h-9 rounded-control-sm text-small' : 'h-10 text-control max-md:text-small',
            className
          )}
        >
          {triggerIcons.length ? (
            <span className="flex shrink-0 items-center gap-1">
              {triggerIcons.map(({ value, icon: Icon, iconClassName, iconStyle }) =>
                Icon ? (
                  <Icon
                    key={value}
                    aria-hidden
                    style={iconStyle}
                    className={cn('size-3.5 stroke-2', iconClassName)}
                  />
                ) : null
              )}
            </span>
          ) : null}
          <span className={cn('min-w-0 flex-1 truncate', !selected.length && 'text-ink-placeholder')}>
            {only?.label ?? (selected.length ? summary(selected.length) : placeholder)}
          </span>
          <ChevronDownIcon
            aria-hidden
            className={cn(
              'size-4 shrink-0 stroke-2 text-ink-subtle transition-transform',
              open && 'rotate-180'
            )}
          />
        </button>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={6}
          collisionPadding={8}
          data-slot="select-content"
          aria-label={ariaLabel}
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            document.getElementById(listId)?.focus();
          }}
          className={cn(
            'z-[90] flex w-max max-w-[calc(100vw-16px)] min-w-[max(var(--radix-popover-trigger-width),240px)] flex-col rounded-block border border-line-input bg-white p-1 shadow-popover outline-none',
            'max-h-[min(360px,var(--radix-popover-content-available-height))]',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0'
          )}
        >
          <div
            id={listId}
            role="listbox"
            aria-label={ariaLabel}
            aria-multiselectable
            aria-activedescendant={options[active] ? optionId(active) : undefined}
            tabIndex={-1}
            onKeyDown={onListKey}
            className="flex min-h-0 flex-col gap-px overflow-y-auto outline-none"
          >
            {options.map((o, i) => {
              const on = values.includes(o.value);
              const Icon = o.icon;

              return (
                <div
                  key={o.value}
                  id={optionId(i)}
                  role="option"
                  aria-selected={on}
                  tabIndex={-1}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => toggle(o)}
                  onKeyDown={onListKey}
                  onMouseEnter={() => setActive(i)}
                  data-slot="select-item"
                  className={cn(
                    'flex min-h-8.5 cursor-pointer items-center gap-2.5 rounded-control-sm px-2.5 py-1.5 text-ink select-none',
                    size === 'sm' ? 'text-small' : 'text-control max-md:text-small',
                    i === active && 'bg-surface-muted',
                    on && 'font-semibold'
                  )}
                >
                  <span
                    aria-hidden
                    className={cn(
                      'grid size-4 shrink-0 place-items-center rounded-[4px] border',
                      on ? 'border-primary bg-primary text-white' : 'border-line-input bg-white'
                    )}
                  >
                    {on ? <CheckIcon className="size-3 stroke-[3]" /> : null}
                  </span>
                  {Icon ? (
                    <Icon
                      aria-hidden
                      style={o.iconStyle}
                      className={cn('size-4 shrink-0 stroke-2', o.iconClassName)}
                    />
                  ) : null}
                  <span className="min-w-0 flex-1">{o.label}</span>
                  {o.hint !== undefined ? (
                    <span
                      data-numeric
                      className="inline-grid h-5 min-w-5 shrink-0 place-items-center rounded-pill bg-surface-muted px-1.5 text-micro font-semibold text-ink-secondary"
                    >
                      {o.hint}
                    </span>
                  ) : null}
                </div>
              );
            })}
          </div>
          {values.length ? (
            <div className="mt-1 border-t border-line-divider pt-1">
              <button
                type="button"
                onClick={() => onValuesChange([])}
                className="h-8 w-full rounded-control-sm px-2.5 text-left text-small font-semibold text-primary hover:bg-surface-muted"
              >
                Limpar seleção
              </button>
            </div>
          ) : null}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
